import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PlantacionDeInforme } from "@/lib/informes";
import { formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { plantasTotalesDelFormulario } from "./cuadroDePlantaciones";
import { SelectorDeEspecie } from "./SelectorDeEspecie";
import type { FormularioDePlantacion } from "./plantaciones";

type ClaveDelFormulario = keyof FormularioDePlantacion;

interface CamposDePlantacionProps {
  /**
   * Prefijo de los `id` de los controles.
   *
   * EL FORMULARIO EN LÍNEA Y EL MODAL DE EDICIÓN CONVIVEN EN EL DOM. Con el
   * modal abierto, el formulario de la lista puede seguir montado detrás, así
   * que sin prefijos distintos habría dos controles con el mismo `id` y
   * `getByLabelText` (o cualquier tecnología asistiva) encontraría dos.
   *
   * NO SE LO PASA A `SelectorDeEspecie`: ese campo genera su propio `id` con
   * `useId()`, así que dos instancias montadas a la vez ya reciben
   * identificadores distintos sin que nadie se lo pida.
   */
  idPrefijo: string;
  formulario: FormularioDePlantacion;
  ocupada: boolean;
  escribir: (cambio: Partial<FormularioDePlantacion>) => void;
}

interface CamposDeIdentificacionProps extends CamposDePlantacionProps {
  /**
   * Las filas del informe contra las que `SelectorDeEspecie` arma sus
   * sugerencias ("es solo UX", sin maestro de especies).
   *
   * QUIEN LLAMA DECIDE QUÉ FILAS ENTRAN: el formulario de alta manda TODAS —la
   * que se está por crear no existe todavía—; el modal manda todas MENOS la
   * fila que se está corrigiendo. Sin excluirla, la comparación "¿ya existe
   * esta especie?" encontraría la PROPIA fila y siempre daría verdadero.
   */
  filas: PlantacionDeInforme[];
  /** Los obligatorios: se resaltan en ámbar mientras estén vacíos. */
  requeridos?: ClaveDelFormulario[];
  /**
   * Muestra la nota de terreno. El formulario del diseño no la pide, pero la
   * fila que ya la trae cargada la sigue mostrando: ocultarla dejaría un dato
   * guardado sin forma de verlo ni de corregirlo.
   */
  conNotaDeTerreno?: boolean;
}

/**
 * Identificación: especie (con sus sugerencias), variedad, año de
 * plantación, superficie, cuartel y portainjerto.
 *
 * NO HAY NINGÚN CAMPO DE VALOR, vida útil o ajuste, al revés que sus hermanas
 * con cuadro: esto describe el huerto, no lo tasa. Lo que una plantación
 * aporta al predio sale del método de activos biológicos (NIC 41), que todavía
 * no está construido.
 *
 * LA SUPERFICIE VA EN HECTÁREAS Y NO EN LA UNIDAD DEL INFORME: su campo vecino
 * es la densidad en plantas POR HECTÁREA, y el producto solo cierra si las dos
 * hablan de lo mismo. La unidad está impresa en el rótulo.
 */
export function GrupoDeIdentificacion({
  idPrefijo,
  formulario,
  ocupada,
  escribir,
  filas,
  requeridos = [],
  conNotaDeTerreno = false,
}: CamposDeIdentificacionProps) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave],
    faltante: requeridos.includes(clave) && formulario[clave].trim() === "",
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div className="col-span-2 min-w-0">
        <SelectorDeEspecie
          valor={formulario.especie}
          // Las sugerencias salen de las filas de ESTE informe: no hay
          // maestro de especies y no hace falta ("es solo UX").
          filas={filas}
          disabled={ocupada}
          faltante={requeridos.includes("especie") && formulario.especie.trim() === ""}
          onChange={(especie) => escribir({ especie })}
        />
      </div>

      <Campo
        id={`${idPrefijo}-variedad`}
        etiqueta="Variedad"
        placeholder="Ej: Sweet Heart"
        clase="col-span-2"
        {...campo("variedad")}
      />

      <Campo
        id={`${idPrefijo}-anio`}
        etiqueta="Año plantación"
        placeholder="Ej: 2016"
        inputMode="numeric"
        {...campo("anio_plantacion")}
      />

      <Campo
        id={`${idPrefijo}-superficie`}
        etiqueta="Superficie"
        unidad="há"
        placeholder="Ej: 4,30"
        inputMode="decimal"
        {...campo("superficie_ha")}
      />

      <Campo
        id={`${idPrefijo}-cuartel`}
        etiqueta="Cuartel"
        placeholder="Ej: 6A"
        {...campo("cuartel")}
      />

      <Campo
        id={`${idPrefijo}-portainjerto`}
        etiqueta="Portainjerto"
        placeholder="Ej: Colt"
        {...campo("portainjerto")}
      />

      {conNotaDeTerreno ? (
        <Campo
          id={`${idPrefijo}-nota`}
          etiqueta="Nota de terreno"
          placeholder=""
          clase="col-span-2 lg:col-span-4"
          {...campo("nota_terreno")}
        />
      ) : null}
    </div>
  );
}

/**
 * Datos de la plantación: distancia, densidad y sistema de riego. Todo
 * opcional al crear.
 */
export function GrupoDeDatos({ idPrefijo, formulario, ocupada, escribir }: CamposDePlantacionProps) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave],
    faltante: false,
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      {/* TEXTO Y NO DOS NÚMEROS: "4,5 x 2,0" son la distancia entre hileras y
          entre plantas, y se dictan como una sola expresión. */}
      <Campo
        id={`${idPrefijo}-distancia`}
        etiqueta="Distancia plantación"
        placeholder="Ej: 4,5 x 2,0"
        clase="col-span-2"
        {...campo("distancia_plantacion")}
      />

      {/* "pl/há" y no "Plantas" a secas: es la DENSIDAD y no el total del
          cuartel, que es justo la confusión que este cuadro provoca en el
          documento de origen (multiplicarla otra vez por la superficie infla
          el total en un factor igual a las hectáreas del cuartel). */}
      <Campo
        id={`${idPrefijo}-densidad`}
        etiqueta="Plantas por há"
        placeholder="Ej: 1.111"
        inputMode="numeric"
        {...campo("plantas_por_ha")}
      />

      <Campo
        id={`${idPrefijo}-riego`}
        etiqueta="Sistema de riego"
        placeholder="Ej: Microaspersión"
        clase="col-span-2"
        {...campo("sistema_riego")}
      />
    </div>
  );
}

/**
 * Las "Plantas totales": lo que el servidor va a calcular con lo escrito.
 *
 * La cuenta es la del servidor (`plantasTotalesDelFormulario`, su gemelo), así
 * que la cifra que se ve es la que va a quedar guardada, con el mismo
 * redondeo. "—" y nunca `0` mientras falte la superficie o la densidad.
 */
export function PlantasTotales({ formulario }: { formulario: FormularioDePlantacion }) {
  const total = plantasTotalesDelFormulario(formulario);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-3.5 py-2.5",
        total === null ? "bg-superficie-sutil border-divisor" : "bg-success-soft border-success-dot/40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase">
          Plantas totales
        </div>
        <p className="text-ink-muted mt-0.5 text-[0.6875rem] font-semibold text-pretty">
          {total === null
            ? "Se calcula al completar superficie y plantas por há."
            : "Superficie × plantas por há, redondeado al entero más cercano."}
        </p>
      </div>
      <span
        className={cn("flex-none font-mono text-lg font-bold", total === null ? "text-ink-ghost" : "text-ink")}
      >
        {formatearMonto(total, null)}
      </span>
    </div>
  );
}

/**
 * Un campo de texto del formulario: rótulo (con su unidad) e input.
 *
 * Existe porque los campos de texto de esta pestaña son la MISMA estructura
 * repetida: escrita a mano, un `htmlFor` mal copiado no lo notaría nadie — el
 * campo seguiría viéndose bien y solo dejaría de responder al clic en su
 * rótulo.
 */
function Campo({
  id,
  etiqueta,
  unidad,
  placeholder,
  valor,
  clase,
  faltante,
  disabled,
  inputMode,
  onChange,
}: {
  id: string;
  etiqueta: string;
  unidad?: string;
  placeholder: string;
  valor: string;
  clase?: string;
  faltante: boolean;
  disabled: boolean;
  inputMode?: "numeric" | "decimal";
  onChange: (valor: string) => void;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", clase)}>
      <Label htmlFor={id} className="mb-0 items-center gap-1.5">
        {etiqueta}
        {unidad ? <span className="text-ink-hint font-semibold">{unidad}</span> : null}
      </Label>
      <Input
        id={id}
        inputMode={inputMode}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(faltante && "border-warning-dot/60")}
      />
    </div>
  );
}
